import { z } from 'zod'

const currencySchema = z.enum(['USD', 'EUR', 'GBP', 'INR', 'AED', 'CAD', 'AUD', 'JPY', 'SGD', 'CHF'])
const paymentSourceSummarySchema = z.object({
  id: z.string().uuid(),
  source_type: z.enum(['stripe_card', 'stripe_ach', 'manual_invoice', 'wire', 'purchase_order']),
  display_label: z.string().min(1).max(160),
  status: z.enum(['active', 'disabled', 'expired']),
  currency: currencySchema,
  is_primary: z.boolean(),
  expires_at: z.string().datetime().nullable(),
}).strict()
const invoiceSummarySchema = z.object({
  id: z.string().uuid(),
  invoiceNumber: z.string().min(1).max(80),
  subscriptionId: z.string().uuid().nullable(),
  status: z.enum(['draft', 'open', 'paid', 'void', 'uncollectible', 'partially_paid']),
  currency: currencySchema,
  amountMinorUnits: z.number().int().nonnegative(),
  taxAmountMinorUnits: z.number().int().nonnegative(),
  appliedCreditMinorUnits: z.number().int().nonnegative(),
  totalDueMinorUnits: z.number().int().nonnegative(),
  periodStart: z.string().datetime().nullable(),
  periodEnd: z.string().datetime().nullable(),
  issuedAt: z.string().datetime().nullable(),
  dueAt: z.string().datetime().nullable(),
  paidAt: z.string().datetime().nullable(),
  paymentTerms: z.string().min(1),
  sourceOrigin: z.string().min(1),
  createdAt: z.string().datetime(),
}).strict()

export const billingHttpReadAutoTopupConfigResponseSchema = z.object({
  contract_version: z.literal('1.0.0'),
  config: z.object({
    enabled: z.boolean(),
    thresholdMinorUnits: z.number().int().positive().nullable(),
    amountMinorUnits: z.number().int().positive().nullable(),
    maxPerPeriodMinorUnits: z.number().int().positive().nullable(),
    periodConsumedMinorUnits: z.number().int().nonnegative(),
    currency: currencySchema,
    disabledAt: z.string().datetime().nullable(),
    disabledReason: z.string().nullable(),
  }).strict(),
  primaryPaymentSource: paymentSourceSummarySchema.nullable(),
}).strict()

export const billingHttpReadAutoTopupAttemptsResponseSchema = z.object({
  contract_version: z.literal('1.0.0'),
  attempts: z.array(z.object({
    id: z.string().uuid(),
    triggeredAt: z.string().datetime(),
    triggerReason: z.string().min(1),
    balanceAtTriggerMinorUnits: z.number().int(),
    amountAttemptedMinorUnits: z.number().int(),
    status: z.enum(['pending', 'succeeded', 'failed', 'skipped']),
    skipReason: z.string().nullable(),
    invoiceId: z.string().uuid().nullable(),
    paymentId: z.string().uuid().nullable(),
    completedAt: z.string().datetime().nullable(),
  }).strict()),
  nextCursor: z.string().min(1).optional(),
}).strict()

export const billingHttpReadInvoiceDetailResponseSchema = z.object({
  contract_version: z.literal('1.0.0'),
  invoice: invoiceSummarySchema.extend({
    orgId: z.string().regex(/^\d+$/),
    voidedAt: z.string().datetime().nullable(),
    lineItems: z.array(z.object({
      description: z.string().min(1),
      quantity: z.number().int().positive(),
      unitAmountMinorUnits: z.number().int().nonnegative(),
      amountMinorUnits: z.number().int().nonnegative(),
    }).strict()),
    lineItemsRedacted: z.boolean(),
    updatedAt: z.string().datetime().nullable(),
  }).strict(),
}).strict()

export const billingHttpReadInvoicesResponseSchema = z.object({
  contract_version: z.literal('1.0.0'),
  invoices: z.array(invoiceSummarySchema),
  nextCursor: z.string().min(1).optional(),
}).strict()

export const billingHttpReadBillingCreditsResponseSchema = z.object({
  contract_version: z.literal('1.0.0'),
  credits: z.array(z.object({
    id: z.string().uuid(),
    amountMinorUnits: z.number().int(),
    currency: currencySchema,
    reason: z.string().min(1),
    createdAt: z.string().datetime(),
    expiresAt: z.string().datetime().optional(),
    applied: z.boolean(),
  }).strict()),
}).strict()

export const billingHttpReadBillingEventsResponseSchema = z.object({
  contract_version: z.literal('1.0.0'),
  events: z.array(z.object({
    id: z.string().uuid(),
    eventType: z.string().min(1),
    actor: z.string().min(1),
    actorId: z.string().nullable(),
    eventData: z.record(z.unknown()),
    region: z.string().min(1),
    occurredAt: z.string().datetime(),
  }).strict()),
  nextCursor: z.string().min(1).optional(),
}).strict()

export const billingHttpReadSubscriptionResponseSchema = z.object({
  contract_version: z.literal('1.0.0'),
  subscription: z.object({
    id: z.string().uuid(),
    planKey: z.string().min(1),
    status: z.enum(['trialing', 'active', 'past_due', 'paused', 'canceled', 'expired']),
    currency: currencySchema,
    currentPeriodStart: z.string().datetime(),
    currentPeriodEnd: z.string().datetime(),
    trialEnd: z.string().datetime().nullable(),
    autoTopupEnabled: z.boolean(),
    primaryPaymentSourceId: z.string().uuid().nullable(),
  }).strict().nullable(),
}).strict()

export const billingHttpReadPaymentSourcesResponseSchema = z.object({
  contract_version: z.literal('1.0.0'),
  sources: z.array(paymentSourceSummarySchema),
}).strict()

export type BillingHttpReadAutoTopupConfigResponse = z.infer<typeof billingHttpReadAutoTopupConfigResponseSchema>
export type BillingHttpReadAutoTopupAttemptsResponse = z.infer<typeof billingHttpReadAutoTopupAttemptsResponseSchema>
export type BillingHttpReadInvoiceDetailResponse = z.infer<typeof billingHttpReadInvoiceDetailResponseSchema>
export type BillingHttpReadInvoicesResponse = z.infer<typeof billingHttpReadInvoicesResponseSchema>
export type BillingHttpReadBillingCreditsResponse = z.infer<typeof billingHttpReadBillingCreditsResponseSchema>
export type BillingHttpReadBillingEventsResponse = z.infer<typeof billingHttpReadBillingEventsResponseSchema>
export type BillingHttpReadSubscriptionResponse = z.infer<typeof billingHttpReadSubscriptionResponseSchema>
export type BillingHttpReadPaymentSourcesResponse = z.infer<typeof billingHttpReadPaymentSourcesResponseSchema>
